import React from "react";
import { Navigate } from "react-router-dom";
import { useCRUDPermissions } from "../hooks/usePermission";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { isEmpty, isObject } from "lodash";

/**
 * ProtectedRoute Component
 * 
 * Protects routes based on resource and action permissions.
 * Redirects to dashboard if user doesn't have the required permission.
 * 
 * @param {string} resource - Resource name (e.g., "PROJECTS", "CUSTOMER_PAYMENTS")
 * @param {string|string[]} action - Action name(s) (default: "READ"). Can be an array like ["READ", "CREATE"]
 * @param {React.ReactNode} children - The route component to render
 * @param {string} redirectTo - Where to redirect if no permission (default: "/dashboard")
 */
const ProtectedRoute = ({
  resource,
  action = "READ",
  children,
  redirectTo = "/dashboard",
}) => {
  const { userData } = useSelector((state) => state.auth);
  // Always use useCRUDPermissions which can handle both single actions and arrays
  const actions = Array.isArray(action) ? action : [action];
  const { hasPermission, isLoading } = useCRUDPermissions(resource, actions);

  // Check if user is logged in (has userData with a role)
  const isLoggedIn = isObject(userData) && !isEmpty(userData) && userData?.role;

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect if no permission
  if (!hasPermission) {
    // Only show toast if user is logged in but doesn't have permission
    // Don't show toast if user is logged out (to avoid showing error on login screen)
    if (isLoggedIn) {
      toast.error("You don't have permission to access this page");
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Render the protected route
  return <>{children}</>;
};

export default ProtectedRoute;


